import { cleanShouldShowLastLogin, useApiClient, useAuth, useIdleTimer, useTenantConfiguration } from '@msdk/common'
import { useComponentModal } from '@msdk/forms'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { IdlePrompt } from '../components/IdlePrompt'

/**
 * this global variable is used to prevent the application from invalidating
 * the entire state on logout being triggered, this would cause a lot of request being launched
 * just as the user is being logged out and could cause erratic behaviour
 */
export let SESSION_ENDING = false
const MAX_INACTIVITY = 14400 * 60 * 1000

export function triggerSessionEnding() {
  SESSION_ENDING = true
}

export function useAutoLogOff() {
  const { pathname } = useLocation()
  const auth = useAuth()
  const tenantConfig = useTenantConfiguration()
  const api = useApiClient()

  const lastRenew = useRef<number | null>(null)
  const [prompt, setPrompt] = useState<boolean>(false)

  const modal = useComponentModal()

  useEffect(() => {
    lastRenew.current = Date.now()
  }, [pathname])

  const getRandomNumber = (min: number, max: number) => {
    min = Math.ceil(min) // Ensure min is rounded up
    max = Math.floor(max) // Ensure max is rounded down
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  const inactivityExists =
    tenantConfig?.inactivityTimeout !== null &&
    tenantConfig?.inactivityTimeout !== undefined &&
    tenantConfig?.inactivityTimeout > 0
  /**
   * Check if the tenant has a inactivity time configured, is yes set it to that minus 5 seconds to prevent
   * any possible error caused by trying to logout after the session is expired, if not set it to the max allowed time.
   * The random number generator is there so that logins between multiple tabs are spaced a bit in time instead of triggering
   * all at the same time, this avoids concurrency issues with the logout reuqests.
   */
  const inactivity = inactivityExists
    ? tenantConfig?.inactivityTimeout * 60 * 1000 - getRandomNumber(10, 20) * 1000
    : MAX_INACTIVITY
  const { getRemainingTime, reset } = useIdleTimer({
    timeout: inactivity,
    // As per spec the prompt for idle is triggered 2 minutes before going idle
    promptBeforeIdle: 2 * 60 * 1000,
    onIdle: () => {
      SESSION_ENDING = true
      auth.onLogout(
        {
          extraQueryParams: { idle: true },
        },
        false,
      )
      console.log('User has been logged out due to inactivity.')
      cleanShouldShowLastLogin()
    },
    onAction: (_, __, ___) => {
      if (!prompt) {
        // Trigger the session refresh only if this is the tab the user is interacting with
        const remaining = getRemainingTime()
        const lastRenewElasped = Date.now() - (lastRenew.current || 0)
        const elapsedLimit = 1 * 60 * 1000
        if (remaining < elapsedLimit || lastRenewElasped > elapsedLimit) {
          api.auth.renewSession()
          lastRenew.current = Date.now()
        }
      }
    },
    onPrompt: () => {
      setPrompt(true)
      const modalRef = modal.createModal(<IdlePrompt />, () => {
        reset()
        api.auth.renewSession()
        lastRenew.current = Date.now()
        setPrompt(false)
      })
      modalRef.open()
    },
  })
}
